import { useEffect, useRef, useState } from 'react';
import { Button } from 'lift-ui';
import { jobsErrorMessage } from '../../api';
import { useResolveSyncEmails } from '../../hooks';
import type { ResolveSyncEmailsResult, SyncUser } from '../../types';
import { TYPE_SINGULAR, parseEmails, useDebouncedValue } from './shared';

interface PasteEmailsProps {
  disabled?: boolean;
  onAdd: (users: SyncUser[]) => void;
}

type Ambiguous = ResolveSyncEmailsResult['ambiguous'];

export const PasteEmails = ({ disabled, onAdd }: PasteEmailsProps) => {
  const [text, setText] = useState('');
  const [matched, setMatched] = useState(0);
  const [unresolved, setUnresolved] = useState<string[]>([]);
  const [ambiguous, setAmbiguous] = useState<Ambiguous>([]);
  const [copied, setCopied] = useState(false);
  const resolve = useResolveSyncEmails();
  const latest = useRef(0);
  const debounced = useDebouncedValue(text, 400);
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;

  const run = (value: string) => {
    const emails = parseEmails(value);
    const request = ++latest.current;
    if (emails.length === 0) {
      setMatched(0);
      setUnresolved([]);
      setAmbiguous([]);
      return;
    }

    resolve.mutate(emails, {
      onSuccess: (result) => {
        if (request !== latest.current) return;
        onAddRef.current(result.resolved);
        setMatched(result.resolved.length);
        setUnresolved(result.unresolved);
        setAmbiguous(result.ambiguous);
      },
    });
  };

  useEffect(() => {
    run(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const chooseMatch = (item: Ambiguous[number], match: Ambiguous[number]['matches'][number]) => {
    onAdd([{ id: match.id, type: match.type, name: match.name, email: item.email }]);
    setAmbiguous((current) => current.filter((entry) => entry.email !== item.email));
    setMatched((count) => count + 1);
  };

  const copyUnresolved = async () => {
    try {
      await navigator.clipboard.writeText(unresolved.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <label htmlFor="sync-paste-emails" className="text-xs uppercase tracking-wide text-muted-foreground">
        Paste emails
      </label>
      <textarea
        id="sync-paste-emails"
        rows={5}
        disabled={disabled}
        value={text}
        placeholder="Paste emails, comma or line separated"
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        onChange={(event) => {
          setText(event.target.value);
          setCopied(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            run(text);
          }
        }}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">Matches are added to your selection.</span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || resolve.isPending || parseEmails(text).length === 0}
          onClick={() => run(text)}
        >
          {resolve.isPending ? 'Working…' : 'Match emails'}
        </Button>
      </div>

      <div className="flex flex-col gap-2 text-sm" aria-live="polite">
        {resolve.isError && (
          <p className="text-destructive">{jobsErrorMessage(resolve.error, 'Emails could not be matched.')}</p>
        )}
        {matched > 0 && <p className="tabular-nums">{matched} matched</p>}
        {unresolved.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <p className="tabular-nums">{unresolved.length} not found</p>
              <Button type="button" size="sm" variant="ghost" onClick={copyUnresolved}>
                {copied ? 'Copied' : 'Copy list'}
              </Button>
            </div>
            <ul className="max-h-24 overflow-y-auto rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground" aria-label="Emails not found">
              {unresolved.map((email) => (
                <li key={email} className="break-all">
                  {email}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ambiguous.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="tabular-nums">{ambiguous.length} in more than one type. Choose which to add.</p>
            {ambiguous.map((item) => (
              <div key={item.email} className="flex flex-col gap-1 rounded-md border border-border p-2">
                <span className="break-all text-xs">{item.email}</span>
                <div className="flex flex-wrap gap-1" role="group" aria-label={`Choose type for ${item.email}`}>
                  {item.matches.map((match) => (
                    <Button
                      key={`${match.type}:${match.id}`}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => chooseMatch(item, match)}
                    >
                      {TYPE_SINGULAR[match.type]}
                      {match.name ? ` · ${match.name}` : ''}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
